import {
  count,
  get,
  getClonedDocument,
  getDocumentZip,
  getDocumentsByUserId,
  index,
  post,
  put,
  putBusinessDetails,
  putDocumentRequirement,
  putDocumentStatus,
  putStatus,
  getDocumentMaster,
  getCreditVerification,
  getReport,
  putVerificationData,
  putCreditReportForApproval,
  putCreditReportStatusApproved,
  putCreditReportStatusRejected,
  getAssociatedUsers,
  putApplicationStatus,
  getCourtCasesReport,
  getGstFilingReport,
  getCreditReportByDirectorId,
  putConsentInfo,
  putDocumentSignatureStatus,
  getDocumentSignatureStatus,
  getPortfolio,
  putCreditRating,
  putDirectorDetail,
  putApplicationStage,
  putLinkAssociatedUser,
  putUnLinkAssociatedUser,
  putOperationalStatus,
  putVerifyUserVerificationCode,
} from "src/api/user";

import UserForm from "src/modules/user/components/UserForm.vue";
import TradeNameForm from "src/modules/user/components/TradeNameForm.vue";
import DirectorForm from "src/modules/user/components/DirectorForm.vue";
import DocumentForm from "src/modules/user/components/DocumentForm.vue";
import VerificationCenter from "src/modules/user/components/VerificationCenter.vue";
import PreScreeningDetailView from "src/modules/user/components/PreScreeningDetailView.vue";
import { downloadBlob } from "src/assets/scripts/utilities/helpers";

const getInitialState = () => {
  return {
    associatedUsers: [],
    isAssociatedUser: {
      _id: "",
    },
    counts: {},
    reports: {
      "due-diligence-report": {
        businessSummary: {},
        sections: [],
      },
    },
    user: {
      basicInfo: {},
      businessDetails: {},
      othersInfo: {
        poSetting: {},
      },
      bankInfo: {},
      taxSettingsInfo: {},
      paymentsTermsInfo: {},
    },
    users: [],
    userDocuments: [],
    userDocumentsStatuses: {},
    userVerificationData: {},
  };
};

export const useUserStore = defineStore("user", () => {
  const { state: formState, toggle: toggleUserForm } = useDrawer({
    component: UserForm,
  });
  const { state: tradeNameFormState, toggle: toggleTradeNameForm } = useDrawer({
    component: TradeNameForm,
  });
  const { state: directorFormState, toggle: toggleDirectorForm } = useDrawer({
    component: DirectorForm,
  });
  const { state: documentFormState, toggle: toggleDocumentForm } = useDrawer({
    component: DocumentForm,
  });
  const {
    state: verificationCenterFormState,
    toggle: toggleVerificationCenterForm,
  } = useDrawer({
    component: VerificationCenter,
  });
  const { toggle: togglePreScreeningDetailForm } = useDrawer({
    component: PreScreeningDetailView,
  });

  let state = reactive(getInitialState());

  const groupDocumentsByDirector = (documentList) => {
    const directorMap = new Map();
    const result = [];

    for (const document of documentList) {
      if (directorMap.has(document.director_id)) {
        directorMap.get(document.director_id).push(document);
      } else if (document.director_id) {
        directorMap.set(document.director_id, [document]);
      }
    }

    for (const document of documentList) {
      const group = directorMap.get(document.director_id);

      if (group) {
        result.push(...group);

        directorMap.delete(document.director_id);
      } else if (!document.director_id) {
        result.push(document);
      }
    }

    return result;
  };

  const getUserDocumentsStatusCount = (documentList) => {
    const result = {};

    for (const document of documentList) {
      if (result[document.status_cd] == null) {
        result[document.status_cd] = 1;
      } else {
        result[document.status_cd]++;
      }
    }

    result["all"] = documentList.length;

    return result;
  };

  const buyers = computed(() => {
    return state.users?.filter((user) => user.role === "buyer");
  });

  const kams = computed(() => {
    return state.users?.filter((user) => user.role === "kam");
  });

  const sellers = computed(() => {
    return state.users?.filter((user) => user.role === "seller");
  });

  const setUser = (updatedUser) => {
    const mapDirectors = (directors) => {
      return directors?.length
        ? directors.map((director) => ({
          ...director,
          credit_score: director.credit_score ?? null,
        }))
        : [
          {
            name: "",
            phone: "",
            pan: "",
            aadhar: "",
            date_of_birth: "",
            credit_score: null,
          },
        ];
    };

    const basicInfo = {
      shop_name: updatedUser.shop_name,
      name: updatedUser.name,
      phone: updatedUser.phone,
      role: updatedUser.role,
      email: updatedUser.email,
      business_channel: updatedUser.business_channel,
      trade_name: updatedUser.trade_name,
      distribution_type: updatedUser.distribution_type,
      is_email_verified: updatedUser.is_email_verified,
      is_phone_verified: updatedUser.is_phone_verified,
    };

    const poSetting = {
      po_applicable: Boolean(updatedUser?.po_applicable),
      po_file_applicable: Boolean(updatedUser?.po_file_applicable),
    };

    const primaryAddress = {
      pincode: updatedUser.pincode,
      city: updatedUser.city,
      state: updatedUser.state,
      state_cd: updatedUser.state_cd,
      address: updatedUser.address,
      is_primary: true,
    };

    const businessDetails = {
      gstin: updatedUser.gstin,
      is_gst_registered: updatedUser.is_gst_registered,
      shop_name: updatedUser.shop_name,
      business_type: updatedUser.business_type,
      legal_business_name: updatedUser.legal_business_name ?? "",
      number_of_director: updatedUser.number_of_director,
      gstin_status: updatedUser.gstin_status,
      filing_frequency: updatedUser.filing_frequency,
      latest_gst_filed: updatedUser.latest_gst_filed,
      gst_filing_status_count: updatedUser.gst_filing_status_count,
      registration_date: updatedUser.registration_date,
      incorporation_date: updatedUser.incorporation_date,
      total_gross_income: updatedUser.total_gross_income,
      annual_aggregate_turnover: updatedUser.annual_aggregate_turnover,
      aggregate_turnover_range: updatedUser.aggregate_turnover_range,
      fssai: updatedUser.fssai,
      fssai_expiration_date: updatedUser.fssai_expiration_date,
      udyam_aadhar: updatedUser.udyam_aadhar ?? "",
      msme_category: updatedUser.msme_category ?? "",
      directors: mapDirectors(updatedUser.directors),
      ...primaryAddress,
      is_msme_registered:
        updatedUser.is_msme_registered || !!updatedUser.udyam_aadhar,
      is_trade_msme: updatedUser.is_trade_msme ?? false,
      pan: updatedUser.pan || "",
      mcaRecords: updatedUser?.mcaRecords,
    };

    const othersInfo = {
      account_owner: updatedUser.account_owner,
      associated_agent: updatedUser.associated_agent,
      categories: updatedUser.categories,
      products: updatedUser.products,
      demand_broadcast: updatedUser.demand_broadcast,
    };

    const bankInfo = {
      account_number: updatedUser.account_number,
      bank_beneficiary_name: updatedUser.bank_beneficiary_name,
      ifsc_code: updatedUser.ifsc_code,
      bank_name: updatedUser.bank_name,
      code: updatedUser.code,
      is_primary: updatedUser.code,
      file: updatedUser.file,
    };

    const taxSettingsInfo = {
      tax_settings: updatedUser.tax_settings,
      min_net_sales_margin: updatedUser.min_net_sales_margin,
    };

    const paymentsTermsInfo = {
      credit_cycle: updatedUser.credit_cycle,
      credit_limit: updatedUser.credit_limit,
      credit_eligibility: updatedUser.credit_eligibility,
    };

    const creditSummary = {
      background_check: {
        ...updatedUser.background_check,
        credit_limit: updatedUser.credit_limit,
        credit_eligibility: updatedUser.credit_eligibility,
      },
    };

    state.user = {
      ...updatedUser,
      basicInfo,
      businessDetails,
      primaryAddressDetails: { ...primaryAddress },
      othersInfo,
      bankInfo,
      taxSettingsInfo,
      paymentsTermsInfo,
      creditSummary: { ...creditSummary },
    };

    if (basicInfo.role === "buyer") {
      state.user.othersInfo.poSetting = poSetting;
    }
  };

  const getUser = async (id) => {
    try {
      const response = await get(id);

      setUser(response.data?.data);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getUserPortfolio = async (id) => {
    try {
      const response = await getPortfolio(id);

      return response.data?.data;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const filters = ref({
    roles: null,
    is_internal_user: null,
  });

  const { filter, pagination, params } = usePagination(filters);

  const getUserCounts = async (options) => {
    try {
      const response = await count(options);

      state.counts = response.data?.data;

      return response;
    } catch (error) {
      console.error(error);
    }
  };

  const getUsers = async (options = {}) => {
    try {
      const [, response] = await Promise.all([
        getUserCounts({ params: params.value, ...unref(options) }),
        index({ params: params.value, ...unref(options) }),
      ]);

      state.users = response.data?.data?.data || [];

      pagination.value.rowsNumber = response.data?.data?.count;

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };
  const postUser = async (user) => {
    try {
      if (user?.is_internal_user) {
        const response = await post(user);

        setUser(response.data?.data);

        return response;
      }

      const userResponse = await post(user);

      setUser(userResponse.data?.data);

      return userResponse;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const putUser = async (user) => {
    try {
      const response = await put({
        ...user,
        _id: state.user?._id,
      });

      await getUser(state.user?._id);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const putUserStatus = async (user) => {
    try {
      const { is_active } = user;

      user.is_active = !is_active;

      const response = await putStatus(user);

      setUser(user);

      await getUsers();

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const putUserBusinessDetails = async (user) => {
    try {
      const response = await putBusinessDetails({
        ...user,
        _id: state.user?._id,
      });

      setUser(response.data?.data);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getUserDocuments = async (options = {}) => {
    try {
      const { _id, business_type, is_gst_registered } = state.user;

      const response = await getDocumentsByUserId({
        params: {
          applies_to: business_type?.code,
          groupBySubCategories: options.groupBySubCategories || 0,
          status_cd: options.status_cd,
          is_gst_registered: is_gst_registered,
        },
        user_id: _id,
      });

      const responseUserDocuments = response.data?.data;

      if (options.groupBySubCategories) {
        const computedResponse = [];

        for (const category of responseUserDocuments) {
          let groupedDocuments = groupDocumentsByDirector(category.documents);

          computedResponse.push({ ...category, documents: groupedDocuments });
        }

        state.userDocuments = computedResponse;
      } else {
        let groupedDocuments = groupDocumentsByDirector(responseUserDocuments);

        state.userDocuments = groupedDocuments;

        if (options.status_cd == null) {
          let statusCount = getUserDocumentsStatusCount(groupedDocuments);

          state.userDocumentsStatuses = statusCount;
        }
      }

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const putUserDocumentRequirement = async (document) => {
    try {
      const response = await putDocumentRequirement(document);

      await getUserDocuments();

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const putUserDocumentStatus = async (document) => {
    try {
      const response = await putDocumentStatus(document);

      await getUserDocuments();

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getUserDocumentsZip = async (options = {}) => {
    try {
      const response = await getDocumentZip({
        params: {
          user_id: options.user_id,
          sub_category: options.sub_category,
          user_document_id: options.user_document_id,
        },
        responseType: "arraybuffer",
        ...options,
      });

      const contentDisposition = response.headers.get("Content-Disposition");

      let filename = "download.zip";

      if (contentDisposition?.includes("attachment")) {
        filename = contentDisposition.split("filename=")[1].trim();

        filename = filename.substring(1, filename.length - 1);
      }

      const blob = new Blob([response.data], { type: "application/zip" });

      downloadBlob(blob, filename, false);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getDocumentDetails = async (code) => {
    const response = await getDocumentMaster(code);

    return response;
  };

  const getClonedUserDocument = async (file, options) => {
    try {
      const response = await getClonedDocument(file, options);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const setUserVerificationData = (stageData) => {
    const { stage_name, ...stage_data } = stageData;
    const stageKeyMap = {
      pre_screening: "pre_screening",
      direct_verification: "direct_verification",
      indirect_verification: "indirect_verification",
    };
    const key = stageKeyMap[stage_name];

    if (key) {
      state.userVerificationData = {
        ...state.userVerificationData,
        [key]: stage_data,
      };
    }
  };

  const getUserCreditVerification = async (options = {}) => {
    try {
      const response = await getCreditVerification({
        user_id: state.user._id,
        ...options,
      });

      setUserVerificationData(response?.data?.data);

      await getUser(state.user._id);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getUserReport = async ({ userId, reportType }) => {
    try {
      const response = await getReport({
        userId,
        reportType,
      });

      state.reports[reportType].sections = response?.data?.data?.report || [];

      state.reports[reportType].businessSummary =
        response?.data?.data?.user_id || {};

      return state.reports[reportType];
    } catch (error) {
      console.error(error);
    }
  };

  const putUserVerificationData = async (verificationData = {}) => {
    try {
      const response = await putVerificationData({
        user_id: state.user._id,
        ...verificationData,
      });

      setUserVerificationData(response?.data?.data);

      await getUser(state.user._id);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const putUserCreditReportForApproval = async (report = {}) => {
    try {
      const response = await putCreditReportForApproval({
        user_id: state.user._id,
        ...report,
      });

      await getUser(state.user._id);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const putUserCreditReportStatus = async (report = {}) => {
    try {
      const apiCall =
        report.status_cd === "approved"
          ? putCreditReportStatusApproved
          : putCreditReportStatusRejected;

      const response = await apiCall({ user_id: state.user._id, ...report });

      await getUser(state.user._id);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getAssociatedUser = async (options = {}) => {
    try {
      const response = await getAssociatedUsers({
        params: {
          user_id: state.user._id,
          isAgent: options.isAgent,
        },
        options,
      });

      state.associatedUsers = response.data?.data;

      return response.data?.data;
    } catch (err) {
      console.error(err);
    }
  };

  const setAssociatedUser = async (id) => {
    if (id) {
      state.isAssociatedUser = {
        value: true,
        _id: id,
      };
    } else {
      state.isAssociatedUser = {
        value: false,
        _id: "",
      };
    }
  };

  const putApplicationStatusDetails = async (data) => {
    try {
      const response = await putApplicationStatus(state.user._id, data);

      return response.data?.data;
    } catch (err) {
      console.error(err);
    }
  };

  const putApplicationStageDetails = async (data) => {
    try {
      const response = await putApplicationStage(state.user._id, data);

      return response.data?.data;
    } catch (err) {
      console.error(err);
    }
  };

  const putUserConsentInfo = async (consentInfo) => {
    try {
      const response = await putConsentInfo(state.user._id, consentInfo);

      const updatedConsentInfo = response?.data?.data?.consent;

      state.user.consent = updatedConsentInfo;

      return response.data?.data;
    } catch (err) {
      console.error(err);
    }
  };

  const putUserDocumentSignatureStatus = async (document) => {
    try {
      const response = await putDocumentSignatureStatus(
        state.user._id,
        document,
      );

      await getUserDocuments();

      return response.data?.data;
    } catch (err) {
      console.error(err);
    }
  };

  const getUserDocumentSignatureStatus = async () => {
    try {
      const user_id = state?.user?._id;

      if (!user_id) {
        return null;
      }

      const response = await getDocumentSignatureStatus(user_id);

      return response.data?.data;
    } catch (err) {
      console.error(err);
    }
  };

  const getUserCourtCasesReport = async (userId) => {
    try {
      const response = await getCourtCasesReport(userId);

      return response;
    } catch (err) {
      console.error(err);
    }
  };

  const getUserGstFilingReport = async (userId, gstin) => {
    try {
      const response = await getGstFilingReport(userId, gstin);

      return response;
    } catch (err) {
      console.error(err);
    }
  };

  const getCreditReportOfDirector = async (directorId) => {
    try {
      const response = await getCreditReportByDirectorId(directorId);

      return response;
    } catch (err) {
      console.error(err);
    }
  };

  const putUserCreditRating = async (payload) => {
    const response = await putCreditRating(state.user._id, payload);

    return response;
  };

  const putDirectorDetails = async (director) => {
    const response = await putDirectorDetail(state.user._id, director);

    await getUser(state.user._id);

    return response;
  };

  const putLinkAssociatedUsers = async (payload) => {
    const { isAgent } = payload;

    const response = await putLinkAssociatedUser(state.user._id, payload);

    await getAssociatedUser({ isAgent });

    return response;
  };

  const putUnlinkAssociatedUsers = async (payload) => {
    const { isAgent } = payload;

    const response = await putUnLinkAssociatedUser(state.user._id, payload);

    await getAssociatedUser({ isAgent });

    return response;
  };

  const putOperationalUserStatus = async (payload) => {
    const response = await putOperationalStatus(state.user._id, payload);

    await getUser(state.user._id);

    return response;
  };
  const setPrimaryAddress = (address) => {
    state.user.primaryAddressDetails = address;
  };

  const verifyUserVerificationCode = async (payload) => {
    const response = await putVerifyUserVerificationCode(payload);

    if (response.status == 200) {
      setUser(response.data?.data);
    }

    return response;
  };

  const reset = () => {
    Object.assign(state, getInitialState());
  };

  const resetUser = () => {
    state.user = getInitialState().user;
  };

  return {
    formState,
    tradeNameFormState,
    directorFormState,
    toggleDirectorForm,
    documentFormState,
    verificationCenterFormState,

    toggleUserForm,
    toggleTradeNameForm,
    toggleDocumentForm,
    toggleVerificationCenterForm,
    togglePreScreeningDetailForm,

    filter,
    filters,
    pagination,

    ...toRefs(state),

    kams,
    buyers,
    sellers,

    getUser,
    getUsers,
    postUser,
    putUser,

    getUserPortfolio,
    putUserStatus,

    reset,

    resetUser,
    getUserCounts,
    putUserBusinessDetails,
    getUserDocuments,
    putUserDocumentRequirement,
    putUserDocumentStatus,
    getUserDocumentsZip,
    getDocumentDetails,
    getClonedUserDocument,
    getUserCreditVerification,
    getUserReport,
    putUserVerificationData,
    putUserCreditReportForApproval,
    putUserCreditReportStatus,
    getAssociatedUser,
    putApplicationStatusDetails,
    putApplicationStageDetails,
    putUserConsentInfo,
    getUserCourtCasesReport,
    getUserGstFilingReport,
    getCreditReportOfDirector,
    putUserDocumentSignatureStatus,
    getUserDocumentSignatureStatus,
    putUserCreditRating,
    putDirectorDetails,
    setAssociatedUser,
    putLinkAssociatedUsers,
    putUnlinkAssociatedUsers,
    putOperationalUserStatus,
    setPrimaryAddress,
    verifyUserVerificationCode,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
}
